'use client';

import React, {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { useUserInfo } from '@/context/UserInfoContext';

import { pushBreadcrumb } from './breadcrumbStore';
import { formatAllowedSearch } from './capture';
import { deriveScene } from './ontology/scene';
import type { Scene } from './ontology/types';

/** Stores the current feedback scene derived from baseline resets and tracked-route setters. */
const SceneContext = createContext<Scene | undefined>(undefined);

/** Stores the scene setter used by tracked routes to set authoritative scene context. */
const SetSceneContext = createContext<React.Dispatch<React.SetStateAction<Scene>> | undefined>(
  undefined
);

/**
 * Emits a navigation breadcrumb whenever the route or its allowlisted search
 * params change. Isolated into its own component so its `useSearchParams()` call
 * can sit inside a Suspense boundary, which Next.js requires to keep ancestor
 * routes (e.g. `/_not-found`) statically prerenderable.
 */
const NavigationBreadcrumbTracker: React.FC = () => {
  const pathname: string = usePathname();
  const searchParams = useSearchParams();
  const filteredSearch = formatAllowedSearch(searchParams);

  useEffect(() => {
    /**
     * Records the raw pathname intentionally: app paths contain only resource identifiers and
     * short-lived invite codes meant to be shared, while durable secrets travel in query params
     * and are already dropped by `SEARCH_PARAM_ALLOWLIST`, so no path redaction is applied here.
     */
    pushBreadcrumb({
      kind: 'navigation',
      label: pathname,
      // Only attach a search detail when there is an allowlisted query to record;
      // an empty search omits `detail` rather than serializing `{ search: '' }`.
      ...(filteredSearch ? { detail: { search: filteredSearch } } : {}),
    });
  }, [filteredSearch, pathname]);

  return null;
};

/**
 * Stores feedback scene state centrally, resetting to the baseline scene on
 * pathname or auth changes before tracked routes set richer scene context.
 */
export const SceneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname: string = usePathname();
  const { userInfo } = useUserInfo();
  const isLoggedIn = !!userInfo;
  const [scene, setScene] = useState<Scene>(() => deriveScene(pathname, isLoggedIn));

  useLayoutEffect(() => {
    setScene(deriveScene(pathname, isLoggedIn));
  }, [pathname, isLoggedIn]);

  return (
    <SetSceneContext.Provider value={setScene}>
      <SceneContext.Provider value={scene}>
        <Suspense fallback={null}>
          <NavigationBreadcrumbTracker />
        </Suspense>
        {children}
      </SceneContext.Provider>
    </SetSceneContext.Provider>
  );
};

/** Returns the current feedback scene and requires a surrounding SceneProvider. */
export const useScene = (): Scene => {
  const context = useContext(SceneContext);
  if (context === undefined) {
    throw new Error('useScene must be used within a SceneProvider');
  }
  return context;
};

/** Returns the scene setter for tracked routes that set authoritative scene context. */
export const useSetScene = (): React.Dispatch<React.SetStateAction<Scene>> => {
  const context = useContext(SetSceneContext);
  if (context === undefined) {
    throw new Error('useSetScene must be used within a SceneProvider');
  }
  return context;
};
